import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { usePopupLogin } from '../../hooks/usePopupLogin';
import { 
  Check, 
  ChevronRight, 
  Shield, 
  Building2, 
  User, 
  FileCheck, 
  Sparkles,
  Rocket,
  PartyPopper,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { inviteService } from '@/services/inviteService';
import { authService } from '@/services/authService';
import { toastUtils } from '@/common/utils/toastUtils';
import NotFound from '../NotFound';
import { useNavigationStore } from '@/common/store';
import { requestService } from '@/services/requestService';
import { CompanyRequest } from '@/common/types/request';
import { companyService, RegisterCompanyData, RegisterUserAndCompanyData } from '@/services/companyService';
import { extractDate } from '@/common/utils/dateFormat';
import Config, { config } from '@/config';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { commonService } from '@/services/commonService';
import { removeSpaces, validatePassword, validateLoginId, getBirthGender } from '@/common/utils/commonUtil';
import { semanticColors } from '@/common/styles/colorTokens';
import { colorTokens } from '@/common/styles/colorTokens';
import Avatar from '@/common/components/avatar/Avatar';
import { typography } from '@/common/styles/typographyTokens';
import ViewSwitcher from '@/common/components/viewSwitcher/ViewSwitcher';
import { LabelInput } from '@/common/components/input/LabelInput';
import { Button } from '@/common/components/button/Button';
import { AICompanyInfoModal } from './components/AICompanyInfoModal';
import { FileUploadModal } from './components/FileUploadModal';
import { AILoadingModal } from './components/AILoadingModal';
import { aiService } from '@/services/aiService';
import { fileService } from '@/services/fileService';
import FileUpload from '@/common/components/fileUpload/FileUpload';
import LoadingModal from '@/common/components/modal/LoadingModal';

type RequestUserType = 'MEMBER_AND_HASCOMPANY' | 'MEMBER_AND_NOCOMPANY' | 'NO_MEMBER_AND_NOCOMPANY'

/**
 * 사업자 정보 인터페이스
 */
interface BusinessInfo {
  companyName: string;
  businessNumber: string;
  representative: string;
  corporateNumber: string;
  establishedDate: string;
}

enum PortfolioStep {
  INITIAL,
  SIGNUP,
  COMPANY,
  WELCOME,
  COMPLETE,
  REJECTED
}

const InvitationCompanyInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const {  } = useNavigationStore();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const domain = config.domain;

  const [requestUserType, setRequestUserType] = useState<RequestUserType | undefined>(undefined);

  // 상태 관리
  const [currentStep, setCurrentStep] = useState<PortfolioStep>(PortfolioStep.INITIAL);
  const [differentUser, setDifferentUser] = useState<boolean>(false);
  const [inviteInfo, setInviteInfo] = useState<CompanyRequest | null>(null);
  const [notificationId, setNotificationId] = useState<string | null>(null);
  const [isPageNotFound, setIsPageNotFound] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // 회원가입 관련 상태
  const [isCertifiedPersonal, setIsCertifiedPersonal] = useState<boolean>(false);
  const [certifyData, setCertifyData] = useState<InisisAuthResult | null>(null);
  
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  
  // 회사 정보 관련 상태
  const [companyId, setCompanyId] = useState<number|undefined>(undefined);
  const [companyDataErrors, setCompanyDataErrors] = useState<Record<string, string>>({});
  const [isCompanyEmail, setIsCompanyEmail] = useState<boolean>(true);
  
  // AI 모달 관련 상태
  const [isAIModalOpen, setIsAIModalOpen] = useState<boolean>(false);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState<boolean>(false);
  const [isAILoadingModalOpen, setIsAILoadingModalOpen] = useState<boolean>(false);
  
  // AI 분석으로 채워진 필드들을 추적하는 상태
  const [aiFilledFields, setAiFilledFields] = useState<Record<string, boolean>>({});
  const [isCertifiedCompany, setIsCertifiedCompany] = useState<boolean>(false);
  
  // 사업자 정보 인증 관련 상태
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isBusinessVerified, setIsBusinessVerified] = useState<boolean | undefined>(undefined);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
  
  // 카운트다운 상태
  const [countdown, setCountdown] = useState<number>(5);

  // 유효성 검사 에러 상태
  const [validationErrors, setValidationErrors] = useState<{
    loginId?: string | null;
    password?: string | null;
    confirmPassword?: string | null;
    companyCode?: string | null;
    secondEmail?: string | null;
    businessNumber?: string | null;
    ceoName?: string | null;
  }>({});

  // 아이디 중복검사 상태
  const [isCheckingLoginId, setIsCheckingLoginId] = useState<boolean>(false);
  const [loginIdChecked, setLoginIdChecked] = useState<boolean>(false);

  // 이메일 중복검사 상태
  const [isCheckingEmail, setIsCheckingEmail] = useState<boolean>(false);
  const [emailChecked, setEmailChecked] = useState<boolean>(false);

  // 회사코드 중복검사 상태
  const [isCheckingCompanyCode, setIsCheckingCompanyCode] = useState<boolean>(false);
  const [companyCodeChecked, setCompanyCodeChecked] = useState<boolean>(false);

  const [companyData, setCompanyData] = useState({
    companyName: '',
    companyNameEn: '',
    ceoName: '',
    businessNumber: '',
    corporateNumber: '',
    establishedDate: '',
    ceoEmail: '',
    companyEmail: '',
    companyCode: ''
  });

  // 기존 상태 관리 부분에 추가
  const [formData, setFormData] = useState({
    residentNumber: '',
    name: '',
    phone: '',
    email: '',
    loginId: '',
    password: '',
    passwordConfirm: '',
    userDi: '',
    userCi: '',
    token: ''
  });
  
  // AI 필드에 사용할 색상
  const aiFieldColor = colorTokens.brand[800];

  // 로그인 팝업
  const { openPopup } = usePopupLogin({
    onSuccess: async (data) => {
      setIsLoading(true);
      try {
        const responseUser = await authService.setUserProfile();
        
        if(!responseUser.success || !responseUser.data) {
          toastUtils.error('사용자 정보를 가져오는 데 실패했습니다.');
          return;
        }
        if(!inviteInfo) {
          toastUtils.error('초대 정보를 가져오는 데 실패했습니다.');
          return;
        }

        if(responseUser.data.name !== inviteInfo.receiver.name || (responseUser.data.email !== inviteInfo.receiver.email && !responseUser.data.companyUsers.some((companyUser: any) => companyUser.email === inviteInfo.receiver.email))) {
          toastUtils.error('초대된 사용자와 일치하지 않습니다.');
          setDifferentUser(true);
          return;
        }

        if(inviteInfo.receiver.ceoName && inviteInfo.receiver.businessNumber) {
          const response = await authService.getRepresentativeCompany('portfolioInfo');
          if(!response) {
            toastUtils.error('회사 정보를 가져오는 데 실패했습니다.');
            return;
          }
          const matchedCompanyUser = response.companyUsers.find((companyUser: any) => companyUser.company.businessNumber === inviteInfo.receiver.businessNumber);
          if(matchedCompanyUser) {
            window.location.replace(`${Config.startupClientUrl}/${matchedCompanyUser.company.code}/request/company?n_id=${notificationId}`);
          } else {
            const response = await companyService.checkExistCompany(inviteInfo.receiver.businessNumber);
            if(response.isExist) {
              toastUtils.error('이미 등록된 회사이지만, 귀하는 해당 회사의 권한이 없습니다.');
            } else {
              setRequestUserType('MEMBER_AND_NOCOMPANY');
              setCurrentStep(PortfolioStep.COMPANY);
            }
          }  
        } else {
          setRequestUserType('MEMBER_AND_NOCOMPANY');
          setCurrentStep(PortfolioStep.COMPANY);
        }
      } catch (error) {
        console.error('로그인 처리 중 오류 발생:', error);
        toastUtils.error('로그인 처리 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    }
  });

  // 초기 데이터 로드
  const getInviteInfo = async () => {
    setIsLoading(true);
    try {
      const nId = searchParams.get('n_id');
      if (!nId) {
        setIsPageNotFound(true);
        return;
      }

      setNotificationId(nId);
      const response = await requestService.getRequestCompany(nId);
       
      if (response) {
        setInviteInfo(response);      
        setCurrentStep(PortfolioStep.INITIAL);
      } else {
        setIsPageNotFound(true);
      }
    } catch (error) {
      console.error('초대 정보를 가져오는 중 오류 발생:', error);
      toastUtils.error('초대 정보를 가져오는 데 실패했습니다.');
      setIsPageNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 간편인증 처리
  const handleCertify = async (certifyData: InisisAuthResult) => {
    setIsLoading(true);
    try {
      if(certifyData.isDuplicate) {
        setIsCertifiedPersonal(false);
        setIsLoading(false);
        return toastUtils.error('이미 가입된 유저입니다.');
      }
      
      const userName = (certifyData.userName || '').replace(/\s/g, '');
      const invitedName = (inviteInfo?.receiver?.name || '').replace(/\s/g, '');
      
      if (!userName || !invitedName || userName !== invitedName) {
        toastUtils.error('초대된 사용자와 일치하지 않습니다.');
        setDifferentUser(true);
        setIsLoading(false);
        return;
      }

      setIsCertifiedPersonal(true);
      setCertifyData(certifyData);
      setFormData(prev => ({
        ...prev,
        name: certifyData.userName,
        phone: certifyData.userPhone,
        residentNumber: getBirthGender(certifyData.userBirth, certifyData.userGender)+'******',
        userDi: certifyData.userDi,
        userCi: certifyData.userCi,
        token: certifyData.token
      }));

      setRequestUserType('NO_MEMBER_AND_NOCOMPANY');
      setCurrentStep(PortfolioStep.SIGNUP);
    } catch (error) {
      console.error('인증 처리 중 오류 발생:', error);
      toastUtils.error('인증 처리 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 이메일 유효성 검사
   */
  const validateEmail = (email: string): string | null => {
    if (!email) return '이메일을 입력해주세요.';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return '올바른 이메일 형식을 입력해주세요.';
    }
    
    return null;
  };

  /**
   * 비밀번호 확인 유효성 검사
   */
  const validateConfirmPassword = (password: string, confirmPassword: string): string | null => {
    if (!confirmPassword) return '비밀번호 확인을 입력해주세요.';
    
    if (password !== confirmPassword) {
      return '비밀번호가 일치하지 않습니다.';
    }
    
    return null;
  };

  /**
   * 회사코드 유효성 검사
   */
  const validateCompanyCode = (code: string): string | null => {
    if (!code) return '회사코드를 입력해주세요.';
    
    if (code.includes(' ')) {
      return '회사코드에는 공백을 포함할 수 없습니다.';
    }
    
    const codeRegex = /^[a-zA-Z0-9]+$/;
    if (!codeRegex.test(code)) {
      return '회사코드는 영문과 숫자만 입력 가능합니다.';
    }
    
    return null;
  };

  /**
   * 실시간 유효성 검사 처리
   */
  const handleValidation = (field: string, value: string) => {
    let error: string | null = null;
    
    switch (field) {
      case 'loginId':
        error = validateLoginId(value) as string | null;
        setLoginIdChecked(false);
        break;
      case 'secondEmail':
        error = value.length === 0 ? `${isCompanyEmail ? "개인 이메일을 입력해주세요." : "회사 이메일을 입력해주세요."}` : validateEmail(value);
        setEmailChecked(false);
        break;
      case 'password':
        error = validatePassword(value) as string | null;
        if (passwordConfirm) {
          const confirmError = validateConfirmPassword(value, passwordConfirm);
          setValidationErrors(prev => ({
            ...prev,
            confirmPassword: confirmError
          }));
        }
        break;
      case 'confirmPassword':
        error = validateConfirmPassword(password, value);
        break;
      case 'companyCode':
        error = validateCompanyCode(value);
        setCompanyCodeChecked(false);
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  /**
   * 이메일 중복 검사
   */
  const handleCheckEmail = async () => {
    const email = isCompanyEmail ? formData.email : companyData.companyEmail;
    
    const validationError = validateEmail(email || '');
    if (validationError) {
      setValidationErrors(prev => ({
        ...prev,
        secondEmail: validationError
      }));
      return;
    }
    
    setIsCheckingEmail(true);
    
    try {
      const result = await commonService.checkDuplicateValue('email', email || '');
      
      if (result.exists) {
        setValidationErrors(prev => ({
          ...prev,
          secondEmail: '이미 사용 중인 이메일입니다.'
        }));
        setEmailChecked(false);
        toastUtils.error('이미 사용 중인 이메일입니다.');
      } else {
        setValidationErrors(prev => ({
          ...prev,
          secondEmail: null
        }));
        setEmailChecked(true);
        toastUtils.success('사용 가능한 이메일입니다.');
      }
    } catch (error) {
      console.error('이메일 중복 검사 오류:', error);
      toastUtils.error('중복 검사 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  /**
   * 아이디 중복 검사
   */
  const handleCheckLoginId = async () => {
    const loginId = formData.loginId;
    
    const validationError = validateLoginId(loginId || '');
    if (validationError) {
      setValidationErrors(prev => ({
        ...prev,
        loginId: validationError as string
      }));
      return;
    }
    
    setIsCheckingLoginId(true);
    
    try {
      const result = await commonService.checkDuplicateValue('loginId', loginId || '');
      
      if (result.exists) {
        setValidationErrors(prev => ({
          ...prev,
          loginId: '이미 사용 중인 아이디입니다.'
        }));
        setLoginIdChecked(false);
        toastUtils.error('이미 사용 중인 아이디입니다.');
      } else {
        setValidationErrors(prev => ({
          ...prev,
          loginId: null
        }));
        setLoginIdChecked(true);
        toastUtils.success('사용 가능한 아이디입니다.');
      }
    } catch (error) {
      console.error('아이디 중복 검사 오류:', error);
      toastUtils.error('중복 검사 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingLoginId(false);
    }
  };

  /**
   * 회사코드 중복 검사
   */
  const handleCheckCompanyCode = async () => {
    const code = companyData.companyCode;
    
    const validationError = validateCompanyCode(code);
    if (validationError) {
      setValidationErrors(prev => ({
        ...prev,
        companyCode: validationError
      }));
      return;
    }
    
    setIsCheckingCompanyCode(true);
    
    try {
      const result = await commonService.checkDuplicateValue('companyCode', code);
      
      if (result.exists) {
        setValidationErrors(prev => ({
          ...prev,
          companyCode: '이미 사용 중인 회사코드입니다.'
        }));
        setCompanyCodeChecked(false);
        toastUtils.error('이미 사용 중인 회사코드입니다.');
      } else {
        setValidationErrors(prev => ({
          ...prev,
          companyCode: null
        }));
        setCompanyCodeChecked(true);
        toastUtils.success('사용 가능한 회사코드입니다.');
      }
    } catch (error) {
      console.error('회사코드 중복 검사 오류:', error);
      toastUtils.error('중복 검사 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingCompanyCode(false);
    }
  };

  // AI 모달 핸들러
  const handleAIModalOpen = () => {
    // setIsAIModalOpen(true);
  };

  const handleAIModalClose = () => {
    setIsAIModalOpen(false);
  };

  const handleAIConfirm = () => {
    setIsAIModalOpen(false);
    setIsFileUploadModalOpen(true);
  };

  const handleManualConfirm = () => {
    setIsAIModalOpen(false);
    toastUtils.info('수동으로 기업 정보를 입력합니다.');
  };

  // 파일 업로드 모달 핸들러
  const handleFileUploadModalClose = () => {
    setIsFileUploadModalOpen(false);
  };

  const handleFileUpload = async (file: File) => {
    setIsFileUploadModalOpen(false);
    setIsAILoadingModalOpen(true);

    try {
      const fileResult = await fileService.uploadFile([file]);

      if(!fileResult) return toastUtils.error('파일 업로드에 실패했습니다. 파일을 확인해주세요.');

      const response = await aiService.getAnalyzeBusinessRegistration({
        fileId: fileResult[0].id
      });
      
      if (response) {
        const inviteBusinessNumber = inviteInfo?.receiver?.businessNumber?.replace(/-/g, '') || '';
        const aiBusinessNumber = response.businessNumber.replace(/-/g, '');
        
        if (inviteBusinessNumber !== aiBusinessNumber) {
          toastUtils.error('사업자 번호가 다릅니다.');
          setIsAILoadingModalOpen(false);
          return;
        }

        const updatedCompanyData = {
          ...companyData,
          companyName: response.companyName,
          ceoName: response.representative,
          businessNumber: formatBusinessNumber(response.businessNumber),
          corporateNumber: formatCorporateNumber(response.corporateNumber),
          establishedDate: formatFoundingDate(response.establishmentDate)
        };

        setCompanyData(updatedCompanyData);
        
        setAiFilledFields({
          companyName: true,
          ceoName: true,
          businessNumber: true,
          corporateNumber: true,
          establishedDate: true
        });

        setIsAILoadingModalOpen(false);
        toastUtils.success('AI 분석이 완료되어 기업 정보가 자동으로 입력되었습니다.');
        
      } else {
        throw new Error('AI 분석 실패');
      }
    } catch (error) {
      console.error('AI 분석 중 오류 발생:', error);
      toastUtils.error('AI 분석 중 오류가 발생했습니다. 수동으로 입력해주세요.');
      setIsAILoadingModalOpen(false);
    }
  };

  // AI 로딩 모달 핸들러
  const handleAILoadingModalClose = () => {
    setIsAILoadingModalOpen(false);
  };

  // AI 채움 상태 리셋 함수
  const handleFieldChange = (fieldName: string, value: string) => {
    setCompanyData(prev => ({ ...prev, [fieldName]: value }));
    if (aiFilledFields[fieldName]) {
      setAiFilledFields(prev => ({ ...prev, [fieldName]: false }));
      setIsCertifiedCompany(false);
    }
  };

  // 포맷팅 헬퍼 함수들
  const formatBusinessNumber = (businessNumber: string) => {
    const cleanNumber = businessNumber.replace(/[^0-9]/g, '');
    if (cleanNumber.length === 10) {
      return `${cleanNumber.slice(0,3)}-${cleanNumber.slice(3,5)}-${cleanNumber.slice(5)}`;
    }
    return businessNumber;
  };

  const formatCorporateNumber = (corporateNumber: string) => {
    const cleanNumber = corporateNumber.replace(/[^0-9]/g, '');
    if (cleanNumber.length === 13) {
      return `${cleanNumber.slice(0, 6)}-${cleanNumber.slice(6)}`;
    }
    return corporateNumber;
  };

  const formatFoundingDate = (foundingDate: string) => {
    const cleanDate = foundingDate.replace(/[^0-9]/g, '');
    if (cleanDate.length === 8) {
      return `${cleanDate.slice(0,4)}-${cleanDate.slice(4,6)}-${cleanDate.slice(6)}`;
    }
    return foundingDate;
  };

  /**
   * 사업자 정보 검증 (초대 정보와 비교)
   */
  const validateBusinessInfo = (data: BusinessInfo) => {
    const errors: typeof validationErrors = {};
    const invitedCeoName = inviteInfo?.receiver?.ceoName?.split(",").map(name => name.trim().toUpperCase()) || [];
    const representative = data.representative.split(",").map(name => name.trim().toUpperCase());
    
    let checkedCeoName: string[] = [];
    if(invitedCeoName.length > 0 && representative.length > 0) {
      checkedCeoName = invitedCeoName.filter(name => representative.includes(name));
    }
    
    if(inviteInfo?.receiver?.ceoName && checkedCeoName.length === 0) {
      errors.ceoName = '초대된 대표자와 일치하지 않습니다.';
    }

    const invitedBusinessNumber = inviteInfo?.receiver?.businessNumber?.replace(/-/g, '') || '';
    const inputBusinessNumber = data.businessNumber.replace(/-/g, '');
    
    if(inviteInfo?.receiver?.businessNumber && invitedBusinessNumber !== inputBusinessNumber) {
      errors.businessNumber = '초대된 사업자번호와 일치하지 않습니다.';
    }

    return { isValid: !Object.values(errors).some(error => error !== null && error !== undefined), errors };
  };

  /**
   * 사업자 정보의 완성도 체크
   */
  const isBusinessInfoComplete = (info: BusinessInfo | null): boolean => {
    if (!info) return false;
    return !!(info.companyName && info.businessNumber && info.representative && 
             info.corporateNumber && info.establishedDate);
  };

  /**
   * 현재 사업자 인증 상태 확인
   * 1: 인증완료, 2: AI 정보 부족, 3: 초대정보와 다름
   */
  const getBusinessVerificationState = (): 1 | 2 | 3 => {
    if (isBusinessVerified === true) return 1; // 인증완료
    
    if (!isBusinessInfoComplete(businessInfo)) return 2; // AI 정보 부족
    
    // 초대정보와 다름 (정보는 있지만 검증 실패)
    if (validationErrors.businessNumber || validationErrors.ceoName) return 3;
    
    return 2; // 기본값
  };

  /**
   * 사업자 정보 자동 인증 (국세청 API)
   */
  const autoCheckCompanyStatus = async (data: BusinessInfo) => {
    try {
      const representative = data.representative.split(",").map(name => name.trim());
      let isExistCompany = false;
      let matchedCeoName = '';
      
      for(let i = 0; i < representative.length; i++) {
        const statusResponse = await companyService.checkCompanyStatus({
          companyName: data.companyName,
          ceoName: representative[i],
          businessNumber: data.businessNumber.replace(/-/g, ''),
          corporateNumber: data.corporateNumber.replace(/-/g, ''),
          establishedDate: extractDate(data.establishedDate).replace(/-/g, '')
        });

        if (statusResponse.isApiError) {
          toastUtils.error('국세청 api 오류로 지연되고 있습니다. 1시간 후에 다시 시도해 주세요');
          setIsBusinessVerified(false);
          return;
        }

        if(statusResponse.isExistCompany) {
          isExistCompany = true;
          matchedCeoName = representative[i];
          break;
        }
      }

      if(isExistCompany) {
        setIsBusinessVerified(true);
        setIsCertifiedCompany(true);
        setCompanyData(prev => ({
          ...prev,
          companyName: data.companyName,
          ceoName: matchedCeoName,
          businessNumber: data.businessNumber,
          corporateNumber: data.corporateNumber,
          establishedDate: data.establishedDate,
        }));
        toastUtils.success('사업자 정보 인증이 완료되었습니다.');
      } else {
        toastUtils.error('사업자 정보 인증이 실패하였습니다. 법인등록번호 또는 설립일이 정확한지 확인해주세요.');
        setIsBusinessVerified(false);
      }
    } catch (error) {
      console.error('자동 회사 상태 확인 중 오류:', error);
      setIsBusinessVerified(false);
    }
  };

  /**
   * 사업자등록증 파일 업로드 후 AI 인증 처리
   */
  const handleVerification = async () => {
    if (isVerifying) {
      return;
    }

    if (!uploadedFile) {
      toastUtils.error('사업자등록증 파일을 먼저 업로드해주세요.');
      return;
    }
    
    setIsVerifying(true);
    try {
      const fileResult = await fileService.uploadFile([uploadedFile]);

      if(!fileResult) {
        toastUtils.error('파일 업로드에 실패했습니다. 파일을 확인해주세요.');
        setIsVerifying(false);
        return;
      }
      
      const result = await aiService.getAnalyzeBusinessRegistration({
        fileId: fileResult[0].id
      });

      if(result) {
        const data: BusinessInfo = {
          companyName: result.companyName,
          businessNumber: result.businessNumber,
          representative: result.representative,
          corporateNumber: result.corporateNumber,
          establishedDate: result.establishmentDate,
        };
        
        setBusinessInfo(data);

        if(data.companyName && data.businessNumber && data.representative && data.corporateNumber && data.establishedDate) {
          const validation = validateBusinessInfo(data);
          if(!validation.isValid) {
            setValidationErrors(prev => ({ ...prev, ...validation.errors }));
            setIsBusinessVerified(false);
          } else {
            setValidationErrors(prev => {
              const { businessNumber, ceoName, ...rest } = prev;
              return rest;
            });
            await autoCheckCompanyStatus(data);
          }
        } else {
          setIsBusinessVerified(false);
        }
      } else {
        toastUtils.error('AI 분석에 실패했습니다. 파일을 확인해주세요.');
        setIsBusinessVerified(false);
      }  
    } catch (error) {
      console.error('AI 분석 API 오류:', error);
      toastUtils.error('AI 분석 중 오류가 발생했습니다. 다시 시도해주세요.');
      setIsBusinessVerified(false);
    } finally {
      setIsVerifying(false);
    }
  };

  const checkCompanyStatus = async () => {
    const response = await companyService.checkCompanyStatus({
      companyName: companyData.companyName,
      ceoName: companyData.ceoName,
      businessNumber: companyData.businessNumber.replace(/-/g, ''),
      corporateNumber: companyData.corporateNumber.replace(/-/g, ''),
      establishedDate: extractDate(companyData.establishedDate).replace(/-/g, '')
    });
    if (response.isExistCompany) {
      if (!isCertifiedCompany) {
        toastUtils.success('기업 정보 인증이 완료되었습니다.');
      }
      setIsCertifiedCompany(true);
    } else if (response.isApiError) {
      toastUtils.error('국세청 api 오류로 지연되고 있습니다. 1시간 후에 다시 시도해 주세요');
    } else {
      toastUtils.error('기업 정보 인증이 불가합니다. 입력한 정보를 확인하시거나 잠시 후 다시 시도하세요.');
    }
    return response;
  };

  // 회사 정보 입력 완료
  const handleCompanyComplete = async () => {
    // 사업자 정보가 인증되지 않은 경우
    if (!isBusinessVerified) {
      toastUtils.error('사업자 정보 인증을 먼저 완료해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      if(requestUserType === 'NO_MEMBER_AND_NOCOMPANY') {
        const bodyData:RegisterUserAndCompanyData = {
          notificationId: notificationId!,
          companyEmail: companyData.companyEmail,
          companyEmailVerified: true,
          companyCode: companyData.companyCode,
          isVerified: true,
          isPreStartup: false,
          isCeo: formData.name === companyData.ceoName,
          companyName: companyData.companyName,
          companyNameEn: companyData.companyNameEn || undefined,
          businessNumber: companyData.businessNumber,
          corporateNumber: companyData.corporateNumber,
          isBusinessVerified: true,
          foundingDate: formatFoundingDate(companyData.establishedDate),
          ceoName: companyData.ceoName,
          ceoEmail: companyData.ceoEmail,
          residentNumber: formData.residentNumber,
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          emailVerified: true,
          loginId: formData.loginId,
          password: formData.password,
          userDi: formData.userDi,
          userCi: formData.userCi,
          token: formData.token
        }
        const response = await companyService.registerUserAndCompany(bodyData);
        if(response.success) {
          setCurrentStep(PortfolioStep.WELCOME);
        } else {
          toastUtils.error('회원 가입 중 오류가 발생했습니다.');
        }
      }else if(requestUserType === 'MEMBER_AND_NOCOMPANY') {
        const bodyData:RegisterCompanyData = {
          notificationId: notificationId!,
          companyEmail: companyData.companyEmail,
          companyEmailVerified: true,
          companyCode: companyData.companyCode,
          isVerified: true,
          isPreStartup: false,
          isCeo: user?.name === companyData.ceoName,
          companyName: companyData.companyName,
          companyNameEn: companyData.companyNameEn || undefined,
          businessNumber: companyData.businessNumber,
          corporateNumber: companyData.corporateNumber,
          isBusinessVerified: true,
          foundingDate: formatFoundingDate(companyData.establishedDate),
          ceoName: companyData.ceoName,
          ceoEmail: companyData.ceoEmail
        }
        const response = await companyService.registerCompany(bodyData);
        if(response.success) {
          setCurrentStep(PortfolioStep.WELCOME);
        } else {
          toastUtils.error('회사 등록 중 오류가 발생했습니다.');
        }
      }
    } catch (error) {
      console.error('회사 정보 확인 중 오류 발생:', error);
      toastUtils.error('회사 정보 확인 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 초대 거절
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toastUtils.error('거절 사유를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await inviteService.rejectInvite({
        notificationId: notificationId!,
        reason: rejectReason
      });

      if (response?.success) {
        getInviteInfo();
        setCurrentStep(PortfolioStep.REJECTED);
        toastUtils.success('초대가 성공적으로 거절되었습니다.');
      } else {
        throw new Error('거절 처리 실패');
      }
    } catch (error) {
      console.error('초대 거절 중 오류 발생:', error);
      toastUtils.error('초대 거절 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // firstInit 함수
  const firstInit = async (inviteInfo: CompanyRequest) => {
    try {
      setFormData(prev => ({...prev, name: inviteInfo.receiver.name}));
      setCompanyData(prev => ({...prev, ceoName: inviteInfo.receiver.ceoName || "", businessNumber: inviteInfo.receiver.businessNumber || ""}));

      if(user) {
        if(user.name === inviteInfo.receiver.name && (user.email === inviteInfo.receiver.email || user.companyUsers.some((companyUser: any) => companyUser.email === inviteInfo.receiver.email))) {
          if(inviteInfo.receiver.ceoName && inviteInfo.receiver.businessNumber) {
            const response = await authService.getRepresentativeCompany('portfolioInfo');
            if(!response) {
              toastUtils.error('회사 정보를 가져오는 데 실패했습니다.');
              return;
            }
            const matchedCompanyUser = response.companyUsers.find((companyUser: any) => companyUser.company.businessNumber === inviteInfo.receiver.businessNumber);

            if(matchedCompanyUser) {
              window.location.replace(`${Config.startupClientUrl}/${matchedCompanyUser.company.code}/request/company?n_id=${notificationId}`);
            } else {
              const response = await companyService.checkExistCompany(inviteInfo.receiver.businessNumber);
              if(response.isExist) {
                toastUtils.error('이미 등록된 회사이지만, 귀하는 해당 회사의 권한이 없습니다.');
              } else {
                setRequestUserType('MEMBER_AND_NOCOMPANY');
                setCurrentStep(PortfolioStep.COMPANY);
              }
            }  
          } else {
            setRequestUserType('MEMBER_AND_NOCOMPANY');
            setCurrentStep(PortfolioStep.COMPANY);
          }
        }
      } else {      
        setCurrentStep(PortfolioStep.INITIAL);
      }  
    } catch (error) {
      console.error('초기화 중 오류 발생:', error);
      toastUtils.error('페이지 초기화 중 오류가 발생했습니다.');
    }
  };

  const navigateToStartupSpace = () => {
    window.location.replace(`${Config.startupClientUrl}/${companyData.companyCode}/request/company?n_id=${notificationId}`);
  };

  useEffect(() => {
    if (inviteInfo) {
      firstInit(inviteInfo);
    }
  }, [inviteInfo, user]);

  useEffect(() => {
    
    getInviteInfo();
  }, []);

  useEffect(() => {
    if (currentStep === PortfolioStep.COMPANY) {
      handleAIModalOpen();
    }
  }, [currentStep]);

  // AI로 채워진 필드들이 모두 true일 때 자동으로 회사 상태 확인
  useEffect(() => {
    const allAIFieldsFilled = aiFilledFields.companyName && 
                              aiFilledFields.ceoName && 
                              aiFilledFields.businessNumber && 
                              aiFilledFields.corporateNumber && 
                              aiFilledFields.establishedDate;
    
    if (allAIFieldsFilled && companyData.companyName && companyData.ceoName && 
        companyData.businessNumber && companyData.corporateNumber && companyData.establishedDate) {
      const autoCheckCompanyStatus = async () => {
        try {
          const statusResponse = await companyService.checkCompanyStatus({
            companyName: companyData.companyName,
            ceoName: companyData.ceoName,
            businessNumber: companyData.businessNumber.replace(/-/g, ''),
            corporateNumber: companyData.corporateNumber.replace(/-/g, ''),
            establishedDate: extractDate(companyData.establishedDate).replace(/-/g, '')
          });
          if(statusResponse.isExistCompany) {
            setIsCertifiedCompany(true);
          }
        } catch (error) {
          console.error('자동 회사 상태 확인 중 오류:', error);
        }
      };
      
      autoCheckCompanyStatus();
    }
  }, [aiFilledFields, companyData.companyName, companyData.ceoName, companyData.businessNumber, companyData.corporateNumber, companyData.establishedDate]);

  // isCompanyEmail 상태 변경 시 이메일 배치
  useEffect(() => {
    if (inviteInfo?.receiver.email) {
      if (isCompanyEmail) {
        setCompanyData(prev => ({ ...prev, companyEmail: inviteInfo.receiver.email }));
        setFormData(prev => ({ ...prev, email: '' }));
      } else {
        setFormData(prev => ({ ...prev, email: inviteInfo.receiver.email }));
        setCompanyData(prev => ({ ...prev, companyEmail: '' }));
      }
    }
  }, [isCompanyEmail, inviteInfo?.receiver.email]);

  // 성공 단계에서 카운트다운
  useEffect(() => {
    if (currentStep === PortfolioStep.WELCOME) {
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            navigateToStartupSpace();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [currentStep, companyData.companyCode]);

  if (isPageNotFound) return <NotFound />;
  if (!inviteInfo) return (
    <LoadingContainer>
      <LoadingSpinner>
        <div className="spinner"></div>
        <div>초대 정보를 로드 중입니다...</div>
      </LoadingSpinner>
    </LoadingContainer>
  );

  /**
   * 1단계: 초기 화면 (초대 확인)
   */
  const renderInitialStep = () => (
    <WelcomeContainer>
      <WelcomeContent>
        <WelcomeTextSection>
          <WelcomeInvestorBadge>
            <Avatar alt={inviteInfo.sender.companyName} isUser={false} sizes="md" />
            <span>{inviteInfo.sender.companyName}</span>
          </WelcomeInvestorBadge>
          
          <WelcomeTitle>
            {inviteInfo.receiver.name}님,<br />
            <WelcomeWave>👋</WelcomeWave> 반갑습니다.
          </WelcomeTitle>
          
          <WelcomeDescription>
            <strong>{inviteInfo.sender.name}</strong>님이 {inviteInfo.receiver.investee ? `${inviteInfo.receiver.investee}의` : ''} 
            <WelcomeHighlight> 기업정보 제출을 요청</WelcomeHighlight>했습니다.
            <br />계속 진행하시려면 간편 인증을 진행하시거나 로그인을 해주세요.
          </WelcomeDescription>
          
          {differentUser ? (
            <DifferentUserSection>
              <AlertBox>
                <XCircle size={20} />
                <AlertText>
                  초대된 사용자와 다른 사용자입니다. 자동적으로 거절 처리됩니다.
                  <br />
                  <small>* 사유는 초대자에게 전달됩니다.</small>
                </AlertText>
              </AlertBox>
              <RejectTextarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="거절 사유를 입력해주세요"
              />
              <ButtonRow>
                <BackButton onClick={() => setDifferentUser(false)}>
                  다시하기
                </BackButton>
                <PrimaryButton 
                  onClick={handleReject}
                  disabled={!rejectReason.trim() || isLoading}
                >
                  {isLoading ? '처리 중...' : '거절하기'}
                </PrimaryButton>
              </ButtonRow>
            </DifferentUserSection>
          ) : (
            <WelcomeButtonGroup>
              <InisisAuthButton 
                $variant='fill'
                onSuccess={(data) => handleCertify(data)}
                $title='간편 인증으로 시작하기'
                $size='md'
              />
              <SecondaryButton onClick={openPopup} disabled={isLoading}>
                {isLoading ? '처리 중...' : '기존 계정으로 로그인'}
              </SecondaryButton>
            </WelcomeButtonGroup>
          )}
        </WelcomeTextSection>
        
        <WelcomeImageSection>
          <WelcomeIllustration>
            <WelcomeGlowOrb $delay={0} />
            <WelcomeGlowOrb $delay={2} />
            <WelcomeGlowOrb $delay={4} />
            <WelcomeIconGrid>
              <WelcomeFloatingIcon $delay={0}><Rocket size={32} /></WelcomeFloatingIcon>
              <WelcomeFloatingIcon $delay={1}><Sparkles size={28} /></WelcomeFloatingIcon>
              <WelcomeFloatingIcon $delay={2}><Building2 size={30} /></WelcomeFloatingIcon>
            </WelcomeIconGrid>
          </WelcomeIllustration>
        </WelcomeImageSection>
      </WelcomeContent>
    </WelcomeContainer>
  );

  /**
   * 2단계: 회원가입 정보 입력
   */
  const renderSignupStep = () => (
    <CenteredContainer>
      <ContentWrapper>
        {/* 스텝 인디케이터 */}
        <StepIndicator>
          <StepItem $isActive={true}>
            <StepCircle $isActive={true}><Check size={14} /></StepCircle>
            <StepLabel $isActive={true}>본인 확인</StepLabel>
          </StepItem>
          <StepLine $isActive={true} />
          <StepItem $isActive={true} $isCurrent={true}>
            <StepCircle $isActive={true} $isCurrent={true}>2</StepCircle>
            <StepLabel $isActive={true}>정보 입력</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>3</StepCircle>
            <StepLabel $isActive={false}>기업 설정</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>4</StepCircle>
            <StepLabel $isActive={false}>완료</StepLabel>
          </StepItem>
        </StepIndicator>

        <ContentCard>
          <CardHeader>
            <CardIconWrapper>
              <User size={24} />
            </CardIconWrapper>
            <div>
              <CardTitle>{formData.name}님, 잘 부탁드립니다! 🫡</CardTitle>
              <CardDescription>
                팩트시트 사용을 위해 추가 정보를 입력해 주세요.
              </CardDescription>
            </div>
          </CardHeader>

          <FormSection>
            <FormSectionTitle>
              <Check size={16} />
              <span>인증된 정보</span>
            </FormSectionTitle>
            <VerifiedInfoGrid>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>이름</VerifiedInfoLabel>
                <VerifiedInfoValue>{formData.name}</VerifiedInfoValue>
              </VerifiedInfoItem>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>전화번호</VerifiedInfoLabel>
                <VerifiedInfoValue>{formData.phone}</VerifiedInfoValue>
              </VerifiedInfoItem>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>이메일</VerifiedInfoLabel>
                <VerifiedInfoValue>{isCompanyEmail ? companyData.companyEmail : formData.email}</VerifiedInfoValue>
              </VerifiedInfoItem>
            </VerifiedInfoGrid>
          </FormSection>

          <FormSection>
            <FormSectionTitle>
              <Building2 size={16} />
              <span>이메일 구분</span>
            </FormSectionTitle>
            <RadioGroup>
              <RadioLabel>위 이메일 주소가 회사용 계정인가요?</RadioLabel>
              <ViewSwitcher
                options={[{ label: '네, 회사용 계정입니다.' }, { label: '아니오, 개인 계정입니다.' }]}
                selectedIndex={isCompanyEmail ? 0 : 1}
                onChange={(value) => {
                  setIsCompanyEmail(value === 0);
                  setEmailChecked(false);
                }}
              />
            </RadioGroup>
          </FormSection>

          <FormSection>
            <FormSectionTitle>
              <Shield size={16} />
              <span>계정 설정</span>
            </FormSectionTitle>
            
            <FormInputGroup>
              <FormInputRow>
                <LabelInput
                  label={isCompanyEmail ? "개인 이메일 주소" : "회사 이메일 주소"}
                  required
                  value={isCompanyEmail ? formData.email : companyData.companyEmail}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isCompanyEmail) {
                      setFormData(prev => ({ ...prev, email: value }));
                    } else {
                      setCompanyData(prev => ({ ...prev, companyEmail: value }));
                    }
                    handleValidation('secondEmail', value);
                  }}
                  placeholder="address@email.com" 
                  error={!!validationErrors.secondEmail}
                  errorMessage={validationErrors.secondEmail ? validationErrors.secondEmail : ''}
                />
                <CheckButton 
                  type="button"
                  $isChecked={emailChecked}
                  disabled={isCheckingEmail || !(isCompanyEmail ? formData.email : companyData.companyEmail) || !!validationErrors.secondEmail}
                  onClick={handleCheckEmail}
                >
                  {isCheckingEmail ? '확인 중...' : emailChecked ? '확인 완료' : '중복확인'}
                </CheckButton>
              </FormInputRow>

              <FormInputRow>
                <LabelInput
                  label="아이디"
                  required
                  value={formData.loginId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, loginId: value }));
                    handleValidation('loginId', value);
                  }}
                  placeholder="아이디를 입력해주세요" 
                  error={!!validationErrors.loginId}
                  errorMessage={validationErrors.loginId ? validationErrors.loginId : ''}
                />
                <CheckButton 
                  type="button"
                  $isChecked={loginIdChecked}
                  disabled={isCheckingLoginId || !formData.loginId || !!validationErrors.loginId}
                  onClick={handleCheckLoginId}
                >
                  {isCheckingLoginId ? '확인 중...' : loginIdChecked ? '확인 완료' : '중복확인'}
                </CheckButton>
              </FormInputRow>

              <FormGrid>
                <div>
                  <LabelInput
                    label="비밀번호"
                    required
                    type="password"
                    value={password}
                    onChange={(e) => {
                      const value = removeSpaces(e.target.value);
                      setPassword(value);
                      handleValidation('password', value);
                    }}
                    placeholder="비밀번호를 입력하세요" 
                    error={!!validationErrors.password}
                  />
                  {validationErrors.password && (
                    <ErrorMessage>{validationErrors.password}</ErrorMessage>
                  )}
                </div>
                
                <div>
                  <LabelInput
                    label="비밀번호 확인"
                    required
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => {
                      const value = removeSpaces(e.target.value);
                      setPasswordConfirm(value);
                      handleValidation('confirmPassword', value);
                    }}
                    placeholder="비밀번호를 다시 입력하세요" 
                    error={!!validationErrors.confirmPassword}
                  />
                  {validationErrors.confirmPassword && (
                    <ErrorMessage>{validationErrors.confirmPassword}</ErrorMessage>
                  )}
                </div>
              </FormGrid>
            </FormInputGroup>
          </FormSection>
        </ContentCard>

        <NavigationButtonGroup>
          <BackButton onClick={() => setCurrentStep(PortfolioStep.INITIAL)}>
            뒤로 가기
          </BackButton>
          <PrimaryButton 
            onClick={() => {
              if (!emailChecked) {
                toastUtils.error('이메일 중복확인을 완료해주세요.');
                return;
              }
              if (!loginIdChecked) {
                toastUtils.error('아이디 중복확인을 완료해주세요.');
                return;
              }
              if (validationErrors.loginId) {
                toastUtils.error('아이디를 확인해주세요.');
                return;
              }
              if (validationErrors.password || validationErrors.confirmPassword) {
                toastUtils.error('비밀번호를 확인해주세요.');
                return;
              }
              if (!formData.loginId) {
                toastUtils.error('아이디를 입력해주세요.');
                return;
              }
              if (!password || !passwordConfirm) {
                toastUtils.error('비밀번호를 입력해주세요.');
                return;
              }
              setFormData(prev => ({...prev, password: password, passwordConfirm: passwordConfirm}));
              setCurrentStep(PortfolioStep.COMPANY);
            }}
            disabled={
              isLoading || 
              !!validationErrors.loginId ||
              !!validationErrors.password || 
              !!validationErrors.confirmPassword || 
              !formData.loginId ||
              !loginIdChecked ||
              !password || 
              !passwordConfirm || 
              !(isCompanyEmail ? formData.email : companyData.companyEmail) || 
              !emailChecked
            }
          >
            다음으로
            <ChevronRight size={18} />
          </PrimaryButton>
        </NavigationButtonGroup>
      </ContentWrapper>
    </CenteredContainer>
  );

  /**
   * 3단계: 회사 정보 설정
   */
  const renderCompanyStep = () => (
    <CenteredContainer>
      <ContentWrapper>
        {/* 스텝 인디케이터 */}
        <StepIndicator>
          <StepItem $isActive={true}>
            <StepCircle $isActive={true}><Check size={14} /></StepCircle>
            <StepLabel $isActive={true}>본인 확인</StepLabel>
          </StepItem>
          <StepLine $isActive={true} />
          <StepItem $isActive={true}>
            <StepCircle $isActive={true}><Check size={14} /></StepCircle>
            <StepLabel $isActive={true}>정보 입력</StepLabel>
          </StepItem>
          <StepLine $isActive={true} />
          <StepItem $isActive={true} $isCurrent={true}>
            <StepCircle $isActive={true} $isCurrent={true}>3</StepCircle>
            <StepLabel $isActive={true}>기업 설정</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>4</StepCircle>
            <StepLabel $isActive={false}>완료</StepLabel>
          </StepItem>
        </StepIndicator>

        <ContentCard>
          <CardHeader>
            <CardIconWrapper>
              <Building2 size={24} />
            </CardIconWrapper>
            <div>
              <CardTitle>기업 정보를 설정해주세요 🏢</CardTitle>
              <CardDescription>
                팩트시트에서 사용할 기업 정보를 입력해주세요.
              </CardDescription>
            </div>
          </CardHeader>

          {/* 사업자 정보 인증 섹션 */}
          <FormSection>
            <FormSectionTitle>
              <FileCheck size={16} />
              <span>사업자 정보 인증</span>
            </FormSectionTitle>

            {(() => {
              const state = isBusinessVerified === undefined ? 'upload' : getBusinessVerificationState();
              
              switch (state) {
                case 'upload':
                  return (
                    <FormInputGroup>
                      <Description>
                        최신 사업자등록증 파일을 업로드하고 인증 버튼을 눌러주세요.
                      </Description>
                      
                      <FileUpload
                        type="md"
                        width="100%"
                        status={isVerifying ? 'uploading' : uploadedFile ? 'success' : 'idle'}
                        files={uploadedFile ? [uploadedFile] : []}
                        disabled={isVerifying}
                        accept=".pdf"
                        maxSize={10 * 1024 * 1024}
                        onChange={(files) => {
                          if (files.length > 0) {
                            setUploadedFile(files[0]);
                          } else {
                            setUploadedFile(null);
                            setIsBusinessVerified(undefined);
                          }
                        }}
                        label="여기에 파일을 끌어다 놓거나 클릭하세요"
                        onError={(error) => {
                          toastUtils.error(error);
                        }}
                      />
                      
                      <Button 
                        $size="md" 
                        $variant="fill"
                        $isDisabled={!uploadedFile || isVerifying}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!isVerifying) {
                            handleVerification();
                          }
                        }}
                        style={{ width: '100%', marginTop: '1rem' }}
                      >
                        {isVerifying ? '인증 중...' : '인증하기'}
                      </Button>
                    </FormInputGroup>
                  );

                case 1: // 인증완료
                  return (
                    <FormInputGroup>
                      <VerificationSuccessBox>
                        <CheckCircle size={20} color="#10b981" />
                        <span>사업자 정보 인증이 완료되었습니다.</span>
                      </VerificationSuccessBox>
                      
                      {businessInfo && (
                        <BusinessInfoBox>
                          <BusinessInfoTitle>
                            <CheckCircle size={16} color="#10b981" />
                            <span>인증된 사업자 정보</span>
                          </BusinessInfoTitle>
                          <FormGrid>
                            <LabelInput
                              label="사업자명"
                              value={businessInfo.companyName}
                              readOnly
                            />
                            <LabelInput
                              label="사업자등록번호"
                              value={businessInfo.businessNumber}
                              readOnly
                            />
                            <LabelInput
                              label="대표자"
                              value={businessInfo.representative}
                              readOnly
                            />
                            <LabelInput
                              label="법인등록번호"
                              value={businessInfo.corporateNumber}
                              readOnly
                            />
                            <LabelInput
                              label="설립일"
                              value={businessInfo.establishedDate}
                              readOnly
                            />
                          </FormGrid>
                        </BusinessInfoBox>
                      )}
                    </FormInputGroup>
                  );

                case 2: // AI 정보 부족
                  return (
                    <FormInputGroup>
                      <WarningBox>
                        <AlertTriangle size={20} color="#f59e0b" />
                        <span>AI 분석이 완료되지 않았거나 일부 정보가 누락되었습니다. 정확한 정보를 직접 입력해주세요.</span>
                      </WarningBox>
                      
                      <BusinessInfoBox>
                        <BusinessInfoTitle>
                          <AlertTriangle size={16} color="#f59e0b" />
                          <span>사업자 정보 직접 입력</span>
                        </BusinessInfoTitle>
                        <FormGrid>
                          <LabelInput
                            label="사업자명"
                            value={businessInfo?.companyName || ''}
                            onChange={(e) => setBusinessInfo(prev => prev ? {...prev, companyName: e.target.value} : {
                              companyName: e.target.value,
                              businessNumber: '',
                              representative: '',
                              corporateNumber: '',
                              establishedDate: ''
                            })}
                            placeholder="사업자명을 입력하세요"
                          />
                          <LabelInput
                            label="사업자등록번호"
                            value={businessInfo?.businessNumber || ''}
                            maxLength={12}
                            onChange={(e) => {
                              const formattedValue = formatBusinessNumber(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, businessNumber: formattedValue} : {
                                companyName: '',
                                businessNumber: formattedValue,
                                representative: '',
                                corporateNumber: '',
                                establishedDate: ''
                              });
                            }}
                            placeholder="000-00-00000"
                            error={!!validationErrors.businessNumber}
                            errorMessage={validationErrors.businessNumber || ''}
                          />
                          <LabelInput
                            label="대표자"
                            value={businessInfo?.representative || ''}
                            onChange={(e) => {
                              setBusinessInfo(prev => prev ? {...prev, representative: e.target.value} : {
                                companyName: '',
                                businessNumber: '',
                                representative: e.target.value,
                                corporateNumber: '',
                                establishedDate: ''
                              });
                            }}
                            placeholder="대표자명을 입력하세요"
                            error={!!validationErrors.ceoName}
                            errorMessage={validationErrors.ceoName || ''}
                          />
                          <LabelInput
                            label="법인등록번호"
                            value={businessInfo?.corporateNumber || ''}
                            maxLength={14}
                            onChange={(e) => {
                              const formattedValue = formatCorporateNumber(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, corporateNumber: formattedValue} : {
                                companyName: '',
                                businessNumber: '',
                                representative: '',
                                corporateNumber: formattedValue,
                                establishedDate: ''
                              });
                            }}
                            placeholder="123456-1234567"
                          />
                          <LabelInput
                            label="설립일"
                            value={businessInfo?.establishedDate || ''}
                            maxLength={10}
                            onChange={(e) => {
                              const formattedValue = formatFoundingDate(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, establishedDate: formattedValue} : {
                                companyName: '',
                                businessNumber: '',
                                representative: '',
                                corporateNumber: '',
                                establishedDate: formattedValue
                              });
                            }}
                            placeholder="YYYY-MM-DD"
                          />
                        </FormGrid>
                        
                        <Button 
                          $size="md" 
                          $variant="fill"
                          $isDisabled={!businessInfo?.companyName || !businessInfo?.businessNumber || 
                                   !businessInfo?.representative || !businessInfo?.corporateNumber || 
                                   !businessInfo?.establishedDate}
                          onClick={() => {
                            if (businessInfo) {
                              const validation = validateBusinessInfo(businessInfo);
                              if (validation.isValid) {
                                setValidationErrors(prev => {
                                  const { businessNumber, ceoName, ...rest } = prev;
                                  return rest;
                                });
                                autoCheckCompanyStatus(businessInfo);
                              } else {
                                setValidationErrors(prev => ({ ...prev, ...validation.errors }));
                              }
                            }
                          }}
                          style={{ width: '100%', marginTop: '1rem' }}
                        >
                          정보 확인하기
                        </Button>
                      </BusinessInfoBox>
                    </FormInputGroup>
                  );

                case 3: // 초대정보와 다름
                  return (
                    <FormInputGroup>
                      <ErrorBox>
                        <XCircle size={20} color="#ef4444" />
                        <span>초대된 회사 정보와 일치하지 않습니다.</span>
                      </ErrorBox>

                      {/* 비교 정보 표시 */}
                      <ComparisonBox>
                        <ComparisonTitle>
                          <AlertTriangle size={16} color="#f59e0b" />
                          <span>정보 불일치 내역</span>
                        </ComparisonTitle>
                        
                        {validationErrors.ceoName && (
                          <ComparisonItem>
                            <ComparisonLabel>대표자 이름</ComparisonLabel>
                            <ComparisonContent>
                              <ComparisonRow>
                                <ComparisonBadge $type="invited">초대된 정보</ComparisonBadge>
                                <ComparisonValue>{inviteInfo?.receiver?.ceoName}</ComparisonValue>
                              </ComparisonRow>
                              <ComparisonRow $isError>
                                <ComparisonBadge $type="input">입력된 정보</ComparisonBadge>
                                <ComparisonValue $isError>{businessInfo?.representative}</ComparisonValue>
                              </ComparisonRow>
                            </ComparisonContent>
                          </ComparisonItem>
                        )}

                        {validationErrors.businessNumber && (
                          <ComparisonItem>
                            <ComparisonLabel>사업자등록번호</ComparisonLabel>
                            <ComparisonContent>
                              <ComparisonRow>
                                <ComparisonBadge $type="invited">초대된 정보</ComparisonBadge>
                                <ComparisonValue>{inviteInfo?.receiver?.businessNumber}</ComparisonValue>
                              </ComparisonRow>
                              <ComparisonRow $isError>
                                <ComparisonBadge $type="input">입력된 정보</ComparisonBadge>
                                <ComparisonValue $isError>{businessInfo?.businessNumber}</ComparisonValue>
                              </ComparisonRow>
                            </ComparisonContent>
                          </ComparisonItem>
                        )}
                      </ComparisonBox>
                      
                      <BusinessInfoBox>
                        <BusinessInfoTitle>
                          <XCircle size={16} color="#ef4444" />
                          <span>검증 실패 정보</span>
                        </BusinessInfoTitle>
                        <FormGrid>
                          <LabelInput
                            label="사업자명"
                            value={businessInfo?.companyName || ''}
                            onChange={(e) => setBusinessInfo(prev => prev ? {...prev, companyName: e.target.value} : {
                              companyName: e.target.value,
                              businessNumber: '',
                              representative: '',
                              corporateNumber: '',
                              establishedDate: ''
                            })}
                            placeholder="사업자명을 입력하세요"
                          />
                          <LabelInput
                            label="사업자등록번호"
                            value={businessInfo?.businessNumber || ''}
                            maxLength={12}
                            onChange={(e) => {
                              const formattedValue = formatBusinessNumber(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, businessNumber: formattedValue} : {
                                companyName: '',
                                businessNumber: formattedValue,
                                representative: '',
                                corporateNumber: '',
                                establishedDate: ''
                              });
                            }}
                            placeholder="000-00-00000"
                            error={!!validationErrors.businessNumber}
                            errorMessage={validationErrors.businessNumber || ''}
                          />
                          <LabelInput
                            label="대표자"
                            value={businessInfo?.representative || ''}
                            onChange={(e) => {
                              setBusinessInfo(prev => prev ? {...prev, representative: e.target.value} : {
                                companyName: '',
                                businessNumber: '',
                                representative: e.target.value,
                                corporateNumber: '',
                                establishedDate: ''
                              });
                            }}
                            placeholder="대표자명을 입력하세요"
                            error={!!validationErrors.ceoName}
                            errorMessage={validationErrors.ceoName || ''}
                          />
                          <LabelInput
                            label="법인등록번호"
                            value={businessInfo?.corporateNumber || ''}
                            maxLength={14}
                            onChange={(e) => {
                              const formattedValue = formatCorporateNumber(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, corporateNumber: formattedValue} : {
                                companyName: '',
                                businessNumber: '',
                                representative: '',
                                corporateNumber: formattedValue,
                                establishedDate: ''
                              });
                            }}
                            placeholder="123456-1234567"
                          />
                          <LabelInput
                            label="설립일"
                            value={businessInfo?.establishedDate || ''}
                            maxLength={10}
                            onChange={(e) => {
                              const formattedValue = formatFoundingDate(e.target.value);
                              setBusinessInfo(prev => prev ? {...prev, establishedDate: formattedValue} : {
                                companyName: '',
                                businessNumber: '',
                                representative: '',
                                corporateNumber: '',
                                establishedDate: formattedValue
                              });
                            }}
                            placeholder="YYYY-MM-DD"
                          />
                        </FormGrid>
                        
                        <WarningText>
                          ⚠️ 위 정보가 초대된 회사 정보와 일치하지 않습니다. 올바른 사업자등록증을 다시 업로드해주세요.
                        </WarningText>
                        
                        <Button 
                          $size="md" 
                          $variant="outline"
                          onClick={() => {
                            if (businessInfo) {
                              const validation = validateBusinessInfo(businessInfo);
                              if (validation.isValid) {
                                setValidationErrors(prev => {
                                  const { businessNumber, ceoName, ...rest } = prev;
                                  return rest;
                                });
                                autoCheckCompanyStatus(businessInfo);
                              } else {
                                setValidationErrors(prev => ({ ...prev, ...validation.errors }));
                              }
                            }
                          }}
                          style={{ width: '100%', marginTop: '1rem' }}
                        >
                          다시 인증하기
                        </Button>
                      </BusinessInfoBox>
                    </FormInputGroup>
                  );

                default:
                  return null;
              }
            })()}
          </FormSection>

          {/* 영문 기업명 (인증 완료 후에만 표시) */}
          {isBusinessVerified && (
            <FormSection>
              <FormSectionTitle>
                <Building2 size={16} />
                <span>추가 기업 정보</span>
              </FormSectionTitle>
              <LabelInput 
                label='영문 기업명' 
                value={companyData.companyNameEn}
                onChangeData={(value: string) => setCompanyData(prev => ({ ...prev, companyNameEn: value }))}
                placeholder="영문 기업명을 입력해주세요 (선택)"
              />
            </FormSection>
          )}

          {/* 회사 코드 설정 (인증 완료 후에만 표시) */}
          {isBusinessVerified && (
            <FormSection>
              <FormSectionTitle>
                <Sparkles size={16} />
                <span>회사 코드 설정</span>
              </FormSectionTitle>
              <FormInputRow>
                <LabelInput
                  label='회사 코드'
                  required
                  value={companyData.companyCode}
                  onChangeData={(value: string) => {
                    setCompanyData(prev => ({ ...prev, companyCode: value }));
                    handleValidation('companyCode', value);
                  }}
                  placeholder="영문/숫자로 입력해주세요"
                  error={!!validationErrors.companyCode}
                  errorMessage={validationErrors.companyCode || ''}
                />
                <CheckButton 
                  type="button"
                  $isChecked={companyCodeChecked}
                  disabled={isCheckingCompanyCode || !companyData.companyCode || !!validationErrors.companyCode}
                  onClick={handleCheckCompanyCode}
                >
                  {isCheckingCompanyCode ? '확인 중...' : companyCodeChecked ? '확인 완료' : '중복확인'}
                </CheckButton>
              </FormInputRow>
              <HelperText>
                입력하신 값은 {domain}/{companyData.companyCode || 'example'} 형태로 접속 주소에 사용됩니다.
              </HelperText>
            </FormSection>
          )}
        </ContentCard>

        <NavigationButtonGroup>
          <BackButton onClick={() => {
            if (requestUserType === 'NO_MEMBER_AND_NOCOMPANY') {
              setCurrentStep(PortfolioStep.SIGNUP);
            } else {
              setCurrentStep(PortfolioStep.INITIAL);
            }
          }}>
            뒤로 가기
          </BackButton>
          <PrimaryButton 
            onClick={handleCompanyComplete}
            disabled={
              !isBusinessVerified ||
              !companyData.companyCode ||
              !companyCodeChecked ||
              isLoading
            }
          >
            {isLoading ? '처리 중...' : '가입 완료'}
            <ChevronRight size={18} />
          </PrimaryButton>
        </NavigationButtonGroup>
      </ContentWrapper>

      <LoadingModal
        isOpen={isLoading}
        message="처리 중입니다. 잠시만 기다려주세요."
      />
    </CenteredContainer>
  );

  /**
   * 4단계: 가입 완료 (Welcome)
   */
  const renderWelcomeStep = () => (
    <SuccessContainer>
      <SuccessContent>
        <SuccessIconWrapper>
          <PartyPopper size={48} />
        </SuccessIconWrapper>
        
        <SuccessTitle>회원가입이 완료되었습니다!</SuccessTitle>
        <SuccessDescription>
          축하합니다! 이제 스타트업 스페이스에서 나머지 정보를 입력하고 투자자에게 성공적인 투자를 받기를 기원합니다.
        </SuccessDescription>
        
        <SuccessCard>
          <SuccessCardHeader>
            <SuccessCardIcon>
              <Rocket size={24} />
            </SuccessCardIcon>
            <SuccessCardInfo>
              <SuccessCardTitle>스타트업 스페이스 이동 준비</SuccessCardTitle>
              <SuccessCardDesc>기업 정보 제출 페이지로 이동합니다.</SuccessCardDesc>
            </SuccessCardInfo>
          </SuccessCardHeader>
          
          <SuccessProgressWrapper>
            <SuccessProgressBar>
              <SuccessProgressFill />
            </SuccessProgressBar>
          </SuccessProgressWrapper>
        </SuccessCard>

        <CountdownWrapper>
          <CountdownNumber>{countdown}</CountdownNumber>
          <CountdownText>초 후 자동으로 이동됩니다</CountdownText>
        </CountdownWrapper>

        <SuccessActionButton onClick={navigateToStartupSpace}>
          <span>바로 넘어가기</span>
          <ArrowRight size={18} />
        </SuccessActionButton>
      </SuccessContent>
    </SuccessContainer>
  );

  /**
   * 현재 단계에 따른 컴포넌트 렌더링
   */
  const renderCurrentStep = () => {
    switch (currentStep) {
      case PortfolioStep.INITIAL:
        return renderInitialStep();
      case PortfolioStep.SIGNUP:
        return renderSignupStep();
      case PortfolioStep.COMPANY:
        return renderCompanyStep();
      case PortfolioStep.WELCOME:
        return renderWelcomeStep();
      default:
        return renderInitialStep();
    }
  };

  return (
    <PageContainer>
      {renderCurrentStep()}

      {/* AI 기업 정보 확인 모달 */}
      <AICompanyInfoModal
        isOpen={isAIModalOpen}
        onClose={handleAIModalClose}
        onAIConfirm={handleAIConfirm}
        onManualConfirm={handleManualConfirm}
      />

      {/* 파일 업로드 모달 */}
      <FileUploadModal
        isOpen={isFileUploadModalOpen}
        onClose={handleFileUploadModalClose}
        onFileUpload={handleFileUpload}
      />

      {/* AI 로딩 모달 */}
      <AILoadingModal
        isOpen={isAILoadingModalOpen}
        onClose={handleAILoadingModalClose}
      />
    </PageContainer>
  );
};

// ============================================
// Styled Components
// ============================================

const PageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: ${semanticColors.background.primary};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

// 웰컴 화면 스타일
const WelcomeContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 30% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse at 70% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const WelcomeContent = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    flex-direction: column;
    text-align: center;
    gap: 3rem;
  }
`;

const WelcomeTextSection = styled.div`
  flex: 1;
  max-width: 580px;

  @media (max-width: 968px) {
    max-width: 100%;
  }
`;

const WelcomeInvestorBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 100px;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(8px);
  
  span {
    ${typography.paragraph.p2.medium}
    color: #fff;
  }
  
  @media (max-width: 968px) {
    margin: 0 auto 1.5rem;
  }
`;

const waveAnimation = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(20deg); }
  75% { transform: rotate(-20deg); }
`;

const WelcomeWave = styled.span`
  display: inline-block;
  animation: ${waveAnimation} 1.5s ease-in-out infinite;
  transform-origin: 70% 70%;
`;

const WelcomeTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 1.5rem;
  line-height: 1.3;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const WelcomeHighlight = styled.span`
  background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const WelcomeDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
  line-height: 1.7;

  strong {
    color: #fff;
  }
`;

const WelcomeButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 968px) {
    align-items: center;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.8);
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    color: #fff;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 플로팅 애니메이션
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

// 글로우 펄스 애니메이션
const glowPulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
`;

const WelcomeImageSection = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  
  @media (max-width: 968px) {
    min-height: 300px;
    width: 100%;
  }
`;

const WelcomeIllustration = styled.div`
  position: relative;
  width: 100%;
  max-width: 450px;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WelcomeGlowOrb = styled.div<{ $delay: number }>`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%);
  animation: ${glowPulse} 4s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  
  &:nth-child(1) {
    top: 10%;
    left: 10%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%);
  }
  
  &:nth-child(2) {
    bottom: 20%;
    right: 10%;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%);
  }
  
  &:nth-child(3) {
    top: 40%;
    right: 30%;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%);
  }
`;

const WelcomeIconGrid = styled.div`
  display: flex;
  gap: 2rem;
  position: relative;
  z-index: 1;
`;

const WelcomeFloatingIcon = styled.div<{ $delay: number }>`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  backdrop-filter: blur(8px);
  color: #fff;
  animation: ${floatAnimation} 3s ease-in-out infinite;
  animation-delay: ${props => props.$delay * 0.5}s;
  
  &:nth-child(2) {
    margin-top: 40px;
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.3);
  }
  
  &:nth-child(3) {
    margin-top: -20px;
    background: rgba(16, 185, 129, 0.2);
    border-color: rgba(16, 185, 129, 0.3);
  }
`;

// 다른 사용자 섹션
const DifferentUserSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const AlertBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  color: #fca5a5;
`;

const AlertText = styled.div`
  flex: 1;
  font-size: 0.875rem;
  line-height: 1.5;
  
  small {
    display: block;
    margin-top: 0.5rem;
    opacity: 0.8;
  }
`;

const RejectTextarea = styled.textarea`
  width: 100%;
  height: 120px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #fff;
  font-family: inherit;
  font-size: 0.875rem;
  resize: none;
  outline: none;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
`;

// 센터 컨테이너
const CenteredContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 2rem;
  background: ${semanticColors.background.primary};
`;

const ContentWrapper = styled.div`
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// 스텝 인디케이터
const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const StepItem = styled.div<{ $isActive?: boolean; $isCurrent?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StepCircle = styled.div<{ $isActive?: boolean; $isCurrent?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${typography.paragraph.p2.medium}
  transition: all 0.3s ease;
  
  background: ${props => 
    props.$isActive 
      ? semanticColors.background.brand 
      : semanticColors.background.secondary};
  color: ${props => 
    props.$isActive 
      ? '#fff' 
      : semanticColors.text.tertiary};
  border: 2px solid ${props => 
    props.$isActive 
      ? semanticColors.background.brand 
      : semanticColors.border.primary};
  
  ${props => props.$isCurrent && `
    box-shadow: 0 0 0 4px ${semanticColors.background.brand}20;
  `}
`;

const StepLabel = styled.span<{ $isActive?: boolean }>`
  ${typography.caption.caption1.default}
  color: ${props => props.$isActive ? semanticColors.text.primary : semanticColors.text.tertiary};
  white-space: nowrap;
  
  @media (max-width: 640px) {
    display: none;
  }
`;

const StepLine = styled.div<{ $isActive?: boolean }>`
  width: 40px;
  height: 2px;
  margin: 0 0.75rem;
  background: ${props => 
    props.$isActive 
      ? semanticColors.background.brand 
      : semanticColors.border.primary};
  transition: background 0.3s ease;
  
  @media (max-width: 640px) {
    width: 24px;
  }
`;

// 콘텐츠 카드
const ContentCard = styled.div`
  background: ${semanticColors.background.secondary};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.tertiary};
`;

const CardIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.brand}10;
  border-radius: 12px;
  color: ${semanticColors.background.brand};
  flex-shrink: 0;
`;

const CardTitle = styled.h2`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin: 0 0 0.25rem 0;
`;

const CardDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

// 폼 섹션
const FormSection = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
`;

const FormSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 1rem;
  
  svg {
    color: ${semanticColors.background.brand};
  }
`;

const VerifiedInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const VerifiedInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const VerifiedInfoLabel = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const VerifiedInfoValue = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

const FormInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormInputRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  
  > div:first-child {
    flex: 1;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const RadioGroup = styled.div`
  margin-bottom: 0.5rem;
`;

const RadioLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${semanticColors.text.primary};
  margin-bottom: 0.5rem;
`;

const CheckButton = styled.button<{ $isChecked?: boolean }>`
  padding: 0.625rem 1rem;
  margin-top: 1.75rem;
  border: 1px solid ${props => 
    props.$isChecked ? semanticColors.background.success.primary : semanticColors.background.brand};
  border-radius: 8px;
  background: ${props => 
    props.$isChecked ? semanticColors.background.success.primary : semanticColors.background.brand};
  color: #fff;
  ${typography.paragraph.p2.medium}
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 90px;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VerifyButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const HelperText = styled.div`
  font-size: 0.875rem;
  color: ${semanticColors.text.tertiary};
  margin-top: 0.5rem;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

// 네비게이션 버튼 그룹
const NavigationButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
  
  @media (max-width: 640px) {
    flex-direction: column-reverse;
  }
`;

const BackButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  background: transparent;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.secondary};
    border-color: ${semanticColors.border.secondary};
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${semanticColors.background.brand};
  border: none;
  border-radius: 10px;
  color: #fff;
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 로딩 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: ${semanticColors.background.primary};
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: ${semanticColors.text.primary};
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid ${semanticColors.border.primary};
    border-radius: 50%;
    border-top-color: ${semanticColors.background.brand};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// 성공 화면 스타일
const SuccessContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 50% 30%, rgba(139, 92, 246, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SuccessContent = styled.div`
  max-width: 500px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const successIconPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 20px rgba(139, 92, 246, 0); }
`;

const SuccessIconWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  border-radius: 50%;
  color: #fff;
  animation: ${successIconPulse} 2s ease-in-out infinite;
`;

const SuccessTitle = styled.h1`
  ${typography.heading.h3}
  color: #fff;
  margin-bottom: 0.75rem;
`;

const SuccessDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
`;

const SuccessCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(8px);
`;

const SuccessCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SuccessCardIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  color: #c4b5fd;
`;

const SuccessCardInfo = styled.div`
  text-align: left;
`;

const SuccessCardTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: #fff;
  margin: 0;
`;

const SuccessCardDesc = styled.p`
  ${typography.caption.caption1.default}
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const SuccessProgressWrapper = styled.div`
  padding-top: 0.5rem;
`;

const SuccessProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
`;

const progressAnimation = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

const SuccessProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #8b5cf6 0%, #c4b5fd 100%);
  border-radius: 3px;
  animation: ${progressAnimation} 5s ease-in-out;
`;

const CountdownWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const countdownPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const CountdownNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #c4b5fd;
  animation: ${countdownPulse} 1s ease-in-out infinite;
`;

const CountdownText = styled.div`
  ${typography.paragraph.p2.default}
  color: rgba(255, 255, 255, 0.6);
`;

const SuccessActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #fff;
  border: none;
  border-radius: 12px;
  color: #1e1b4b;
  ${typography.paragraph.p1.medium}
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.2);
  }
`;

// 사업자 정보 인증 관련 스타일
const Description = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin-bottom: 1rem;
  line-height: 1.6;
`;

const VerificationSuccessBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: #d1fae5;
  border: 1px solid #34d399;
  border-radius: 8px;
  
  span {
    color: #065f46;
    font-weight: 500;
    ${typography.paragraph.p2.medium}
  }
`;

const BusinessInfoBox = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const BusinessInfoTitle = styled.h3`
  color: ${semanticColors.text.primary};
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  
  span {
    color: #92400e;
    font-weight: 500;
    ${typography.paragraph.p2.default}
  }
`;

const ErrorBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 8px;
  
  span {
    color: #991b1b;
    font-weight: 500;
    ${typography.paragraph.p2.medium}
  }
`;

const ComparisonBox = styled.div`
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const ComparisonTitle = styled.h3`
  color: #991b1b;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ComparisonItem = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ComparisonLabel = styled.div`
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
`;

const ComparisonContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ComparisonRow = styled.div<{ $isError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: ${props => props.$isError ? '#fee2e2' : '#f0f9ff'};
  border: 1px solid ${props => props.$isError ? '#fca5a5' : '#bae6fd'};
`;

const ComparisonBadge = styled.span<{ $type: 'invited' | 'input' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 80px;
  justify-content: center;
  
  ${props => props.$type === 'invited' ? `
    background-color: #dbeafe;
    color: #1e40af;
    border: 1px solid #93c5fd;
  ` : `
    background-color: #fee2e2;
    color: #991b1b;
    border: 1px solid #fca5a5;
  `}
`;

const ComparisonValue = styled.div<{ $isError?: boolean }>`
  flex: 1;
  font-weight: 500;
  color: ${props => props.$isError ? '#dc2626' : '#1f2937'};
  font-size: 0.9rem;
`;

const WarningText = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background-color: #fef2f2;
  border-radius: 0.5rem;
  border: 1px solid #fecaca;
  color: #dc2626;
  font-size: 0.9rem;
`;

export default InvitationCompanyInfoPage;
